import { Component, EventEmitter, OnInit, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CartComponent } from '../../components/cart/cart.component';
import { CartService } from '../../service/cart.service';

@Component({
  selector: 'app-step5-cart',
  standalone: true,
  imports: [CommonModule, CartComponent],
  templateUrl: './step5-cart.component.html',
  styleUrl: './step5-cart.component.scss'
})
export class Step5CartComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cartContainer', { read: ElementRef, static: false }) cartContainerRef?: ElementRef;

  @Output() finish = new EventEmitter<void>();
  @Output() addMore = new EventEmitter<void>();
  
  showTimeoutModal = false;
  showPaymentFailedModal = false;
  
  private isDragging = false;
  private startY = 0;
  private scrollTop = 0;
  private scrollElement?: HTMLElement;
  private boundMouseDown?: (e: MouseEvent) => void;
  private boundMouseMove?: (e: MouseEvent) => void;
  private boundMouseUp?: () => void;
  private boundTouchStart?: (e: TouchEvent) => void;
  private boundTouchMove?: (e: TouchEvent) => void;
  private boundTouchEnd?: () => void;

  constructor(private cartSrv: CartService) {}

  ngOnInit(): void {
    // Verificar si se volvió por timeout del pago
    const paymentTimeout = sessionStorage.getItem('paymentTimeout');
    if (paymentTimeout === 'true') {
      this.showTimeoutModal = true;
      // Limpiar la flag
      sessionStorage.removeItem('paymentTimeout');
    }

    // Verificar si se volvió por pago rechazado o fallido
    const paymentFailed = sessionStorage.getItem('paymentFailed');
    if (paymentFailed === 'true') {
      this.showPaymentFailedModal = true;
      // Limpiar la flag
      sessionStorage.removeItem('paymentFailed');
    }
  }

  ngAfterViewInit(): void {
    // Esperar un poco para que el DOM esté completamente renderizado
    setTimeout(() => {
      this.setupDragToScroll();
    }, 200);
  }

  ngOnDestroy(): void {
    this.removeDragListeners();
  }

  private setupDragToScroll(): void {
    // Buscar el elemento app-cart que tiene el overflow-y según el CSS
    // Primero intentamos usar el ViewChild
    if (this.cartContainerRef?.nativeElement) {
      this.scrollElement = this.cartContainerRef.nativeElement;
    } else {
      // Fallback: buscar directamente en el DOM
      this.scrollElement = document.querySelector('app-cart') as HTMLElement;
    }
    
    if (!this.scrollElement) {
      console.warn('No se pudo encontrar el elemento scrollable del carrito');
      return;
    }

    // Esperar un frame para asegurar que el contenido esté renderizado
    requestAnimationFrame(() => {
      if (!this.scrollElement) return;
      
      // Crear referencias a los métodos bindeados para poder removerlos después
      this.boundMouseDown = this.onMouseDown.bind(this);
      this.boundMouseMove = this.onMouseMove.bind(this);
      this.boundMouseUp = this.onMouseUp.bind(this);
      this.boundTouchStart = this.onTouchStart.bind(this);
      this.boundTouchMove = this.onTouchMove.bind(this);
      this.boundTouchEnd = this.onTouchEnd.bind(this);

      // Eventos de mouse
      this.scrollElement.addEventListener('mousedown', this.boundMouseDown);
      document.addEventListener('mousemove', this.boundMouseMove);
      document.addEventListener('mouseup', this.boundMouseUp);

      // Eventos táctiles para dispositivos móviles
      this.scrollElement.addEventListener('touchstart', this.boundTouchStart, { passive: false });
      this.scrollElement.addEventListener('touchmove', this.boundTouchMove, { passive: false });
      this.scrollElement.addEventListener('touchend', this.boundTouchEnd);
      
      // Agregar cursor grab por defecto
      this.scrollElement.style.cursor = 'grab';
    });
  }

  private removeDragListeners(): void {
    if (!this.scrollElement) return;

    if (this.boundMouseDown) {
      this.scrollElement.removeEventListener('mousedown', this.boundMouseDown);
    }
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
    }
    if (this.boundTouchStart) {
      this.scrollElement.removeEventListener('touchstart', this.boundTouchStart);
    }
    if (this.boundTouchMove) {
      this.scrollElement.removeEventListener('touchmove', this.boundTouchMove);
    }
    if (this.boundTouchEnd) {
      this.scrollElement.removeEventListener('touchend', this.boundTouchEnd);
    }
  }

  private onMouseDown(e: MouseEvent): void {
    if (!this.scrollElement) return;
    
    // No iniciar drag si se hace clic en un botón o elemento interactivo
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('input')) {
      return;
    }

    // Solo iniciar drag si hay scroll disponible
    if (this.scrollElement.scrollHeight <= this.scrollElement.clientHeight) {
      return;
    }

    this.isDragging = true;
    this.startY = e.pageY;
    this.scrollTop = this.scrollElement.scrollTop;
    this.scrollElement.style.cursor = 'grabbing';
    this.scrollElement.style.userSelect = 'none';
    e.preventDefault();
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging || !this.scrollElement) return;
    
    e.preventDefault();
    const y = e.pageY;
    const walk = (y - this.startY) * 2; // Velocidad del scroll (multiplicador de 2 para más sensibilidad)
    const newScrollTop = this.scrollTop - walk;
    
    // Asegurarse de no exceder los límites del scroll
    const maxScroll = this.scrollElement.scrollHeight - this.scrollElement.clientHeight;
    this.scrollElement.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
  }

  private onMouseUp(): void {
    if (!this.scrollElement) return;
    
    this.isDragging = false;
    this.scrollElement.style.cursor = 'grab';
    this.scrollElement.style.userSelect = '';
  }

  private onTouchStart(e: TouchEvent): void {
    if (!this.scrollElement || e.touches.length !== 1) return;
    
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('input')) {
      return;
    }

    // Solo iniciar drag si hay scroll disponible
    if (this.scrollElement.scrollHeight <= this.scrollElement.clientHeight) {
      return;
    }

    this.isDragging = true;
    this.startY = e.touches[0].pageY;
    this.scrollTop = this.scrollElement.scrollTop;
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.isDragging || !this.scrollElement || e.touches.length !== 1) return;
    
    e.preventDefault();
    const y = e.touches[0].pageY;
    const walk = (y - this.startY) * 2;
    const newScrollTop = this.scrollTop - walk;
    
    // Asegurarse de no exceder los límites del scroll
    const maxScroll = this.scrollElement.scrollHeight - this.scrollElement.clientHeight;
    this.scrollElement.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
  }

  private onTouchEnd(): void {
    this.isDragging = false;
  }

  onFinish() {
    if(this.cartSrv.isEmpty()) {
      return;
    }
    this.finish.emit();
  }

  onAddMore() {
    this.addMore.emit();
  }

  closeModal(): void {
    this.showTimeoutModal = false;
    this.showPaymentFailedModal = false;
  }

}
